import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import userClient from 'clients/user';
import configAxiosInterceptors from 'config/axios/interceptors';
import { loadAppScaffold } from 'store/modules/appScaffold/actions';
import { loadMyUser, mergeMyUser } from 'store/modules/myUser/actions';
import { setNetworkReady } from 'store/modules/network/actions';

export const USER_ID_URL = 'https://myfarmbot.com/userId';

interface Props {
  children: JSX.Element;
}

/**
 * A component for Auth0, axios initialisation, and init API calls.
 * When the access token has no `userId` custom claim, the backend still resolves
 * the account (e.g. via verified email on the JWT); we fetch `GET user/me` for the id.
 */
const Auth0TokenHandler = ({ children }: Props) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently, user: auth0User, loginWithRedirect } = useAuth0();

  useEffect(() => {
    configAxiosInterceptors(getAccessTokenSilently, loginWithRedirect);
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (!auth0User) {
      return;
    }
    dispatch(setNetworkReady(true));

    let cancelled = false;
    const resolveAndBootstrap = async () => {
      let userId = +auth0User[USER_ID_URL];
      if (!userId) {
        const identity = await userClient.fetchMe();
        if (cancelled || identity.error || !identity.data) {
          return;
        }
        userId = identity.data.id;
      }
      if (cancelled || !userId) {
        return;
      }
      dispatch(mergeMyUser({ id: userId }));
      dispatch(loadMyUser()); // Load user profile.
      dispatch(loadAppScaffold()); // Load Core API.
    };

    void resolveAndBootstrap();
    return () => {
      cancelled = true;
    };
  }, [auth0User, dispatch]);

  return children;
};

export default Auth0TokenHandler;
